import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import {
  Loader2, TrendingUp, DollarSign, Users, BadgeCheck, BarChart3, Layers, MapPin, Sparkles, Trophy, Building2,
} from 'lucide-react';
import { passAdmin } from '../../services/passApi';

const QUICK_RANGES = [
  { id: 'this_month', label: 'Mes actual' },
  { id: 'last_30', label: 'Últimos 30 días' },
  { id: 'last_7', label: 'Últimos 7 días' },
  { id: 'last_month', label: 'Mes pasado' },
  { id: 'custom', label: 'Personalizado' },
];

function dateRangeFor(id) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  let from, to;
  if (id === 'this_month') {
    from = new Date(today.getFullYear(), today.getMonth(), 1);
    to = today;
  } else if (id === 'last_30') {
    from = new Date(today);
    from.setDate(from.getDate() - 30);
    to = today;
  } else if (id === 'last_7') {
    from = new Date(today);
    from.setDate(from.getDate() - 7);
    to = today;
  } else if (id === 'last_month') {
    from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    to = new Date(today.getFullYear(), today.getMonth(), 0);
  } else {
    return { from: '', to: '' };
  }
  return { from: fmt(from), to: fmt(to) };
}

export default function PassReports() {
  const [quick, setQuick] = useState('this_month');
  const [range, setRange] = useState(() => dateRangeFor('this_month'));
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byCategory, setByCategory] = useState([]);
  const [byCity, setByCity] = useState([]);
  const [topAffiliates, setTopAffiliates] = useState([]);
  const [topBusinesses, setTopBusinesses] = useState([]);

  useEffect(() => {
    if (quick !== 'custom') {
      setRange(dateRangeFor(quick));
    }
  }, [quick]);

  useEffect(() => {
    if (!range.from || !range.to) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  async function load() {
    setLoading(true);
    const params = { from: range.from, to: range.to };
    try {
      const [s, cat, city, ta, tb] = await Promise.all([
        passAdmin.reportSummary(params),
        passAdmin.reportByCategory(params),
        passAdmin.reportByCity(params),
        passAdmin.reportTopAffiliates({ ...params, limit: 10 }),
        passAdmin.reportTopBusinesses({ ...params, limit: 10 }),
      ]);
      setSummary(s.data);
      setByCategory(cat.data.rows || []);
      setByCity(city.data.rows || []);
      setTopAffiliates(ta.data.rows || []);
      setTopBusinesses(tb.data.rows || []);
    } catch (err) {
      toast.error('Error cargando reportes');
    } finally {
      setLoading(false);
    }
  }

  const maxCategory = Math.max(1, ...byCategory.map((r) => r.reservations || 0));
  const maxCity = Math.max(1, ...byCity.map((r) => r.reservations || 0));

  return (
    <DashboardLayout>
      <div className="px-6 py-6">
        <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2 mb-2">
          <BarChart3 className="w-6 h-6 text-teal-600" /> Reportes Pass
        </h1>
        <p className="text-sm text-slate-500 mb-5">Métricas del ecosistema: ingresos, comisiones, top performers.</p>

        {/* Range selector */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {QUICK_RANGES.map((q) => (
              <button
                key={q.id}
                onClick={() => setQuick(q.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  quick === q.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <label className="block">
              <span className="text-xs text-slate-500">Desde</span>
              <input
                type="date" value={range.from}
                onChange={(e) => { setQuick('custom'); setRange((r) => ({ ...r, from: e.target.value })); }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Hasta</span>
              <input
                type="date" value={range.to}
                onChange={(e) => { setQuick('custom'); setRange((r) => ({ ...r, to: e.target.value })); }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* KPIs */}
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <Kpi icon={DollarSign} label="Cobrado online" value={`$${summary.gross_online.toFixed(2)}`} accent="orange" />
                <Kpi icon={TrendingUp} label="Plataforma (estimado)" value={`$${summary.platform_revenue_est.toFixed(2)}`} />
                <Kpi icon={Users} label="A pagar afiliados" value={`$${summary.affiliate_payouts_owed.toFixed(2)}`} />
                <Kpi icon={Building2} label="Pendiente en negocios" value={`$${summary.pending_at_business.toFixed(2)}`} />
                <Kpi icon={Layers} label="Reservaciones" value={summary.reservations_total} />
                <Kpi icon={BadgeCheck} label="Check-ins" value={summary.checkins_total} />
                <Kpi icon={TrendingUp} label="Conversión" value={`${summary.conversion_rate}%`} />
                <Kpi icon={DollarSign} label="Reservas pagadas" value={summary.paid_reservations} />
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-5 mb-5">
              <Panel title="Por categoría" icon={Layers}>
                {byCategory.length === 0 ? (
                  <EmptyHint />
                ) : (
                  <div className="space-y-2">
                    {byCategory.map((r) => (
                      <Bar key={r.category} label={r.category} value={r.reservations} max={maxCategory} extra={`$${r.gross_online.toFixed(2)}`} />
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="Por ciudad" icon={MapPin}>
                {byCity.length === 0 ? (
                  <EmptyHint />
                ) : (
                  <div className="space-y-2">
                    {byCity.map((r) => (
                      <Bar key={r.city} label={r.city} value={r.reservations} max={maxCity} extra={`$${r.gross_online.toFixed(2)}`} />
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              <Panel title="Top afiliados" icon={Trophy}>
                {topAffiliates.length === 0 ? (
                  <EmptyHint />
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr><th className="text-left py-1">Afiliado</th><th className="text-right">Ganado</th><th className="text-right">#</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {topAffiliates.map((a) => (
                        <tr key={a.affiliate_id}>
                          <td className="py-1.5">
                            <div className="text-slate-900 font-semibold">{a.display_name || '—'}</div>
                            <div className="text-xs text-slate-500 font-mono">{a.affiliate_code}</div>
                          </td>
                          <td className="py-1.5 text-right font-bold">${a.earned_net.toFixed(2)}</td>
                          <td className="py-1.5 text-right text-xs text-slate-500">{a.commissions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>

              <Panel title="Top negocios" icon={Sparkles}>
                {topBusinesses.length === 0 ? (
                  <EmptyHint />
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr><th className="text-left py-1">Negocio</th><th className="text-right">Cobrado online</th><th className="text-right">Res</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {topBusinesses.map((b) => (
                        <tr key={b.business_id}>
                          <td className="py-1.5">
                            <div className="text-slate-900 font-semibold">{b.business_name || '—'}</div>
                            <div className="text-xs text-slate-500">{b.people} personas</div>
                          </td>
                          <td className="py-1.5 text-right font-bold">${b.gross_online.toFixed(2)}</td>
                          <td className="py-1.5 text-right text-xs text-slate-500">{b.reservations}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  const color = accent === 'orange' ? 'text-orange-600' : 'text-slate-900';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-slate-400" />
        <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <h3 className="font-semibold text-slate-900 mb-3 inline-flex items-center gap-2">
        <Icon className="w-4 h-4 text-teal-600" /> {title}
      </h3>
      {children}
    </div>
  );
}

function Bar({ label, value, max, extra }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-slate-700 font-medium capitalize">{label}</span>
        <span className="text-slate-500">{value}{extra ? ` · ${extra}` : ''}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function EmptyHint() {
  return <p className="text-sm text-slate-500 italic">Sin datos en este periodo.</p>;
}
